import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Plus, Edit2, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import Loader from '../../components/Loader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roles = [
  { value: 'fondateur', label: 'Fondateur' },
  { value: 'developpeur', label: 'Développeur' },
  { value: 'co-fondateur', label: 'Co-Fondateur' },
  { value: 'admin', label: 'Administrateur' },
  { value: 'moderateur', label: 'Modérateur' },
  { value: 'helper', label: 'Helper' }
];

const AdminStaff = () => {
  const { token } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    roles: ['moderateur'],
    discord_id: '',
    avatar_url: '',
    order: 0
  });

  const normalizeRoles = (member) => {
    if (Array.isArray(member?.roles) && member.roles.length > 0) {
      return member.roles;
    }
    if (member?.role) {
      return [member.role];
    }
    return [];
  };

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API}/staff`);
      setStaff(response.data);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      roles: ['moderateur'],
      discord_id: '',
      avatar_url: '',
      order: 0
    });
    setEditingStaff(null);
  };

  const openDialog = (staffMember = null) => {
    if (staffMember) {
      setEditingStaff(staffMember);
      setFormData({
        name: staffMember.name,
        roles: normalizeRoles(staffMember),
        discord_id: staffMember.discord_id || '',
        avatar_url: staffMember.avatar_url || '',
        order: staffMember.order || 0
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const toggleRole = (roleValue) => {
    setFormData((current) => {
      const hasRole = current.roles.includes(roleValue);
      return {
        ...current,
        roles: hasRole
          ? current.roles.filter((role) => role !== roleValue)
          : [...current.roles, roleValue]
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.roles.length === 0) {
      toast.error('Sélectionne au moins un rôle');
      return;
    }

    const payload = {
      ...formData,
      role: formData.roles[0]
    };
    
    try {
      if (editingStaff) {
        await axios.put(`${API}/admin/staff/${editingStaff.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Membre modifié avec succès !');
      } else {
        await axios.post(`${API}/admin/staff`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Membre ajouté avec succès !');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchStaff();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
      console.error(error);
    }
  };

  const handleDelete = async (staffId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce membre ?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/staff/${staffId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Membre supprimé !');
      fetchStaff();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
      console.error(error);
    }
  };

  if (loading) {
    return <Loader text="Chargement du staff..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-white uppercase tracking-wider flex items-center gap-2">
            <Users className="w-6 h-6 text-violet-400" />
            Gestion du Staff
          </h1>
          <p className="text-gray-400">{staff.length} membre(s)</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={() => openDialog()}
              className="bg-violet-600 hover:bg-violet-500"
              data-testid="add-staff-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </DialogTrigger>
          
          <DialogContent className="bg-[#0a0a0a] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase tracking-wider">
                {editingStaff ? 'Modifier le membre' : 'Ajouter un membre'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label className="text-gray-300">Nom</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nom du membre"
                  required
                  className="bg-black/50 border-white/10 text-white mt-1"
                  data-testid="staff-name-input"
                />
              </div>
              
              <div>
                <Label className="text-gray-300">Rôles / grades</Label>
                <div className="mt-2 space-y-2 rounded-lg border border-white/10 bg-black/30 p-3" data-testid="staff-role-select">
                  {roles.map((role) => {
                    const isChecked = formData.roles.includes(role.value);
                    return (
                      <label
                        key={role.value}
                        className="flex items-center gap-3 rounded-md px-2 py-2 text-sm text-gray-200 transition-colors hover:bg-white/5"
                      >
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() => toggleRole(role.value)}
                        />
                        <span>{role.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              
              <div>
                <Label className="text-gray-300">Discord ID (optionnel)</Label>
                <Input
                  value={formData.discord_id}
                  onChange={(e) => setFormData({ ...formData, discord_id: e.target.value })}
                  placeholder="@username"
                  className="bg-black/50 border-white/10 text-white mt-1"
                  data-testid="staff-discord-input"
                />
              </div>
              
              <div>
                <Label className="text-gray-300">URL de l'avatar (optionnel)</Label>
                <Input
                  value={formData.avatar_url}
                  onChange={(e) => setFormData({ ...formData, avatar_url: e.target.value })}
                  placeholder="https://..."
                  className="bg-black/50 border-white/10 text-white mt-1"
                  data-testid="staff-avatar-input"
                />
              </div>
              
              <div>
                <Label className="text-gray-300">Ordre d'affichage</Label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  className="bg-black/50 border-white/10 text-white mt-1"
                  data-testid="staff-order-input"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setDialogOpen(false)}
                  className="flex-1 border-white/10 text-white hover:bg-white/5"
                >
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 bg-violet-600 hover:bg-violet-500"
                  data-testid="save-staff-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Sauvegarder
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Staff List */}
      {staff.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="glass rounded-xl p-12 text-center"
        >
          <Users className="w-16 h-16 text-violet-500/50 mx-auto mb-4" />
          <p className="text-gray-400">Aucun membre dans l'équipe</p>
          <Button 
            onClick={() => openDialog()}
            className="mt-4 bg-violet-600 hover:bg-violet-500"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter le premier membre
          </Button>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {staff.map((member, index) => (
            <motion.div
              key={member.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="glass rounded-xl p-4 flex items-center gap-4"
              data-testid={`staff-card-${index}`}
            >
              {/* Avatar */}
              {member.avatar_url ? (
                <img 
                  src={member.avatar_url}
                  alt={member.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-violet-600/20 flex items-center justify-center">
                  <span className="text-lg font-heading font-bold text-violet-400">
                    {member.name.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              
              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{member.name}</p>
                <p className="text-violet-400 text-sm uppercase tracking-wider">
                  {normalizeRoles(member).join(' • ')}
                </p>
              </div>
              
              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openDialog(member)}
                  className="p-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                  data-testid={`edit-staff-${index}`}
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(member.id)}
                  className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                  data-testid={`delete-staff-${index}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminStaff;
